import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { api } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  MemberPermission,
  PermissionResourceType,
  usePermissions,
} from '../hooks/usePermissions';

interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
  picture_url: string | null;
}

interface Entity {
  id: string;
  name: string;
}

const RESOURCE_TYPES: PermissionResourceType[] = ['ap_invoice', 'ar_invoice', 'payment', 'expense'];
const ACTIONS = ['view', 'create', 'approve', 'publish'] as const;
type Action = (typeof ACTIONS)[number];

const ALL_ENTITIES = '__all__';

const flagKey: Record<Action, keyof MemberPermission> = {
  view: 'canView',
  create: 'canCreate',
  approve: 'canApprove',
  publish: 'canPublish',
};

/** Default flag values (mirror migration defaults: view/create on, approve/publish off). */
const ACTION_DEFAULT: Record<Action, boolean> = {
  view: true,
  create: true,
  approve: false,
  publish: false,
};

/**
 * Member permissions (owner/admin only). A matrix of resource_type x {view,create,
 * approve,publish} toggles per member, optionally scoped to one entity. Saved via
 * PUT /permissions. Hidden entirely for non-admins. NOTE: a member with NO saved
 * grant for a (resource, scope) behaves as fully allowed (backward-compatible) —
 * toggles only ever take effect once explicitly saved.
 */
export function MemberPermissionsCard() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const [scope, setScope] = useState<string>(ALL_ENTITIES);

  const members = useQuery<Member[]>({
    queryKey: ['ws-members', wsId],
    enabled: !!wsId && isAdmin,
    queryFn: async () => (await api.get<Member[]>(`/workspaces/${wsId}/members`)).data,
  });

  const entities = useQuery<Entity[]>({
    queryKey: ['entities', wsId],
    enabled: !!wsId && isAdmin,
    queryFn: async () => (await api.get<Entity[]>(`/workspaces/${wsId}/entities`)).data,
  });

  const { list, upsert } = usePermissions(wsId, isAdmin);

  // Hidden/read-only for non-admins.
  if (!isAdmin) return null;

  const entityId = scope === ALL_ENTITIES ? null : scope;

  const grantFor = (userId: string, resourceType: string): MemberPermission | undefined =>
    list.data?.find(
      (g) => g.userId === userId && g.resourceType === resourceType && g.entityId === entityId,
    );

  const valueFor = (userId: string, resourceType: string, action: Action): boolean => {
    const g = grantFor(userId, resourceType);
    if (!g) return ACTION_DEFAULT[action];
    return Boolean(g[flagKey[action]]);
  };

  const toggle = (member: Member, resourceType: PermissionResourceType, action: Action) => {
    const current = grantFor(member.id, resourceType);
    const next: Record<Action, boolean> = {
      view: current ? current.canView : ACTION_DEFAULT.view,
      create: current ? current.canCreate : ACTION_DEFAULT.create,
      approve: current ? current.canApprove : ACTION_DEFAULT.approve,
      publish: current ? current.canPublish : ACTION_DEFAULT.publish,
    };
    next[action] = !valueFor(member.id, resourceType, action);
    upsert.mutate({
      userId: member.id,
      resourceType,
      entityId,
      canView: next.view,
      canCreate: next.create,
      canApprove: next.approve,
      canPublish: next.publish,
    });
  };

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium">{t('settings.permissions.title')}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500">{t('settings.permissions.entity')}</span>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="px-2 py-1.5 text-xs rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
            >
              <option value={ALL_ENTITIES}>{t('settings.permissions.allEntities')}</option>
              {entities.data?.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-xs text-ink-500">{t('settings.permissions.hint')}</p>
        {upsert.isSuccess && (
          <div role="status" className="text-xs text-emerald-600">{t('settings.permissions.saved')}</div>
        )}
        {members.isLoading || list.isLoading ? (
          <div className="text-xs text-ink-500">{t('common.loading')}</div>
        ) : !members.data?.length ? (
          <div className="text-xs text-ink-500">{t('settings.permissions.noMembers')}</div>
        ) : (
          <div className="space-y-5">
            {members.data.map((m) => (
              <div key={m.id}>
                <div className="text-sm font-medium mb-1.5">
                  {m.name}
                  <span className="ml-2 text-xs text-ink-500">{m.role}</span>
                </div>
                {(m.role === 'owner' || m.role === 'admin') ? (
                  <div className="text-xs text-ink-400">{t('settings.permissions.adminOnly')}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="text-ink-500">
                          <th className="text-left font-normal py-1 pr-3">{t('settings.permissions.resource')}</th>
                          {ACTIONS.map((a) => (
                            <th key={a} className="font-normal py-1 px-2 text-center">{t(`settings.permissions.${a}`)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {RESOURCE_TYPES.map((rt) => (
                          <tr key={rt} className="border-t border-ink-100 dark:border-ink-800">
                            <td className="py-1.5 pr-3">{t(`settings.permissions.resourceTypes.${rt}`)}</td>
                            {ACTIONS.map((a) => {
                              const on = valueFor(m.id, rt, a);
                              return (
                                <td key={a} className="py-1.5 px-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => toggle(m, rt, a)}
                                    disabled={upsert.isPending}
                                    aria-pressed={on}
                                    aria-label={`${t(`settings.permissions.resourceTypes.${rt}`)} — ${t(`settings.permissions.${a}`)}`}
                                    className={`inline-flex h-5 w-5 items-center justify-center rounded border ${
                                      on
                                        ? 'bg-accent-600 border-accent-600 text-white'
                                        : 'border-ink-300 dark:border-ink-700 text-transparent'
                                    } disabled:opacity-50`}
                                  >
                                    <Check size={12} />
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
